import type { StudioAuth } from "./client";

type EventName = "studio_opened" | "draft_saved" | "pending_change_opened" | "pending_change_accepted"
  | "pending_change_rejected" | "workspace_switched" | "return_visit";
type Workspace = "write" | "world" | "review" | "production" | "collaboration" | "game" | "analytics" | "unknown";
type Properties = Partial<Record<"durationMs" | "result" | "changed" | "charsGenerated" | "charsAccepted" | "actionCount", string | number | boolean | null>>;
type BufferedEvent = {
  eventId: string; eventName: EventName; occurredAtMs: number; sessionId: string; projectId?: string;
  unitId?: string; workspace: Workspace; client: string; locale: string; properties: Properties;
};

const BUFFER_KEY = "xhalo-story:telemetry:v1";
const SESSION_KEY = "xhalo-story:session:v1";
const LAST_ACTIVITY_KEY = "xhalo-story:last-activity:v1";
const SESSION_IDLE_MS = 30 * 60 * 1000;

function storedEvents(): BufferedEvent[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(BUFFER_KEY) ?? "[]") as unknown;
    return Array.isArray(parsed) ? parsed.slice(-200) as BufferedEvent[] : [];
  } catch {
    return [];
  }
}

function session(): { id: string; returning: boolean } {
  const now = Date.now();
  const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY) ?? "0");
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id || now - last > SESSION_IDLE_MS) {
    id = `session_${crypto.randomUUID().replaceAll("-", "")}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
  return { id, returning: last > 0 && now - last > SESSION_IDLE_MS };
}

export function createStoryTelemetry(input: { apiOrigin: string; projectId: string; auth: StudioAuth }) {
  const activeSession = session();
  let buffer = storedEvents();
  const persist = () => localStorage.setItem(BUFFER_KEY, JSON.stringify(buffer.slice(-200)));
  const requestHeaders = () => {
    const result = new Headers({ "Content-Type": "application/json" });
    if (input.auth.previewActor) result.set("X-Preview-Actor", input.auth.previewActor);
    if (input.auth.previewDisplayName) result.set("X-Preview-Display-Name", input.auth.previewDisplayName);
    if (input.auth.branchName) result.set("X-Story-Branch", input.auth.branchName);
    return result;
  };
  const flush = async (keepalive = false) => {
    if (buffer.length === 0 || !navigator.onLine) return;
    const page = buffer.slice(0, 20);
    try {
      const response = await fetch(`${input.apiOrigin.replace(/\/$/u, "")}/api/v1/telemetry/batch`, {
        method: "POST", credentials: "include", keepalive, headers: requestHeaders(), body: JSON.stringify({ events: page }),
      });
      if (!response.ok) return;
      const ids = new Set(page.map((event) => event.eventId));
      buffer = buffer.filter((event) => !ids.has(event.eventId));
      persist();
      if (buffer.length >= 20 && !keepalive) void flush();
    } catch {
      // Product analytics must never block authoring.
    }
  };
  const track = (eventName: EventName, workspace: Workspace, options: { unitId?: string; properties?: Properties } = {}) => {
    buffer.push({
      eventId: `event_${crypto.randomUUID().replaceAll("-", "")}`, eventName, occurredAtMs: Date.now(),
      sessionId: activeSession.id, projectId: input.projectId, ...(options.unitId ? { unitId: options.unitId } : {}),
      workspace, client: "story-studio-web", locale: navigator.language || "und", properties: options.properties ?? {},
    });
    persist();
    if (buffer.length >= 20) void flush();
  };
  const timer = window.setInterval(() => { void flush(); }, 45_000);
  const online = () => { void flush(); };
  const pagehide = () => { void flush(true); };
  window.addEventListener("online", online);
  window.addEventListener("pagehide", pagehide);
  return {
    track,
    returning: activeSession.returning,
    flush,
    dispose: () => {
      window.clearInterval(timer);
      window.removeEventListener("online", online);
      window.removeEventListener("pagehide", pagehide);
      void flush(true);
    },
  };
}
